'use client';

import { Button, Modal, Typography } from 'components';
import { MAX_SIZE_IN_BYTES_250_MB } from 'constants/constants';
import { CloseIcon } from 'lib/svgs/Icons';
import { useLocale, useTranslations } from 'next-intl';
import prettyBytes from 'pretty-bytes';
import { useContextLibraryUpload } from 'providers/ContextLibraryProviders/useContextLibraryUpload';
import {
  ChangeEvent,
  FC,
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState
} from 'react';
import { cn } from 'utils/cn';
import { v4 as uuidv4 } from 'uuid';
import CardFileContext, { IFileContextLibrary } from './CardFileContext';
import FileAddContext from './FileAddContext';

interface IModalUploadContextLibrary {
  open: boolean;
  defaultTags: string[];
  onCancel: () => void;
  refetch: () => void;
}

export const ALLOWED_TYPES_CONTEXT_LIBRARY = [
  'doc',
  'docx',
  'html',
  'json',
  'md',
  'pdf',
  'pptx',
  'txt'
];

const ModalUploadContextLibrary: FC<IModalUploadContextLibrary> = ({
  open,
  defaultTags: defaultTagsUnprocessed = [],
  onCancel,
  refetch
}) => {
  const t = useTranslations();
  const locale = useLocale();

  const [uploading, setUploading] = useState(false);

  const [defaultTags, setDefaultTags] = useState(defaultTagsUnprocessed);

  const { mutateAsync: contextLibraryUpload } = useContextLibraryUpload();

  const [cardsFile, setCardsFiles] = useReducer<
    Reducer<
      IFileContextLibrary[],
      { state?: IFileContextLibrary; type: 'update' | 'add' | 'remove' | 'reset' }
    >
  >((oldState, action) => {
    if (action.type === 'update')
      return oldState.map((state) =>
        state.id === action?.state?.id ? { ...state, ...action.state } : state
      );
    if (action.type === 'add' && action?.state) return [...oldState, action.state];
    if (action.type === 'remove' && action.state)
      return oldState.filter((state) => state.id !== action?.state?.id);
    if (action.type === 'reset') return [];
    return oldState;
  }, []);

  const isUploaded =
    uploading &&
    cardsFile.length > 0 &&
    cardsFile.every((cardFile) => cardFile.status !== 'added') &&
    cardsFile.some((cardFile) => cardFile.status === 'processed');

  const isAllUploaded = useMemo(
    () => cardsFile.length > 0 && cardsFile.every((cardFile) => cardFile.status === 'processed'),
    [cardsFile]
  );

  const isDisabledUpload =
    uploading ||
    !cardsFile.length ||
    cardsFile.every((cardFile) => cardFile.status === 'processed');

  const initFile = useCallback(
    async (file: File) => {
      const fileExtension =
        file?.name
          ?.match(/\.([^.]+)$/)
          ?.at(1)
          ?.toLowerCase() ?? '';
      const fileType = file?.type;

      const cardFile = {
        id: uuidv4(),
        file: file,
        filename: file?.name,
        filesize: prettyBytes(file.size, { locale }),
        percentage: null,
        status: 'added'
      } as IFileContextLibrary;

      setCardsFiles({
        state: cardFile,
        type: 'add'
      });

      if (
        !fileType.startsWith('image/') &&
        !ALLOWED_TYPES_CONTEXT_LIBRARY.includes(fileExtension ?? '')
      )
        return setCardsFiles({
          state: {
            ...cardFile,
            status: 'error-in-type'
          },
          type: 'update'
        });

      if (file?.size > MAX_SIZE_IN_BYTES_250_MB)
        return setCardsFiles({
          state: {
            ...cardFile,
            status: 'error-in-size'
          },
          type: 'update'
        });
    },
    [locale, setCardsFiles]
  );

  const loadFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e?.target?.files;

      if (!!selectedFiles?.length) [...selectedFiles]?.forEach(initFile);
    },
    [initFile]
  );

  const onRemove = useCallback(
    (id: string) => setCardsFiles({ state: { id }, type: 'remove' }),
    [setCardsFiles]
  );

  const onSuccess = useCallback(() => {
    refetch();
    setUploading(false);
  }, [setUploading, refetch]);

  const uploadFile = useCallback(
    async (cardFile: IFileContextLibrary) => {
      if (cardFile.file)
        try {
          setCardsFiles({
            state: {
              ...cardFile,
              percentage: 0,
              status: 'processing'
            },
            type: 'update'
          });
          const response = await contextLibraryUpload({
            file: cardFile.file,
            tags: cardFile?.tags
          });
          if (response.errors?.length) {
            return setCardsFiles({
              state: {
                ...cardFile,
                status: 'error-in-upload'
              },
              type: 'update'
            });
          }

          setCardsFiles({
            state: {
              ...cardFile,
              percentage: 100,
              status: 'processed'
            },
            type: 'update'
          });
          return onSuccess();
        } catch (error) {
          setCardsFiles({
            state: {
              ...cardFile,
              status: 'error-in-upload'
            },
            type: 'update'
          });
          return setUploading(false);
        }
    },
    [contextLibraryUpload, onSuccess]
  );

  const onRetry = useCallback(
    (cardFile: IFileContextLibrary) => {
      setUploading(true);
      setCardsFiles({
        state: {
          ...cardFile,
          status: 'added'
        },
        type: 'update'
      });
      uploadFile(cardFile);
    },
    [uploadFile]
  );

  const onUpdateTags = useCallback(
    (id: string) => (tags: string[]) => {
      setDefaultTags(Array.from(new Set([...defaultTags, ...tags])));
      setCardsFiles({
        state: {
          id,
          tags
        },
        type: 'update'
      });
    },
    [defaultTags]
  );

  const handleUpload = useCallback(async () => {
    try {
      const cardsProcessing = cardsFile.filter((cardFile) => cardFile.status === 'added');
      if (!uploading && !!cardsProcessing?.length) {
        setUploading(true);
        for (const cardFile of cardsProcessing) {
          await uploadFile(cardFile);
        }
        onSuccess();
      }
    } catch (error) {
      setUploading(false);
    }
  }, [cardsFile, onSuccess, setUploading, uploadFile, uploading]);

  const handleCancel = useCallback(() => {
    onCancel();
    setCardsFiles({ type: 'reset' });
  }, [onCancel]);

  useEffect(() => {
    return () => setUploading(false);
  }, [setUploading]);

  useEffect(() => {
    setDefaultTags(defaultTagsUnprocessed);
  }, [defaultTagsUnprocessed]);

  useEffect(() => {
    if (isAllUploaded)
      setTimeout(() => {
        handleCancel();
      }, 1000);
  }, [isAllUploaded]);

  return (
    <Modal
      zDialog={cn('!z-[1999]')}
      zDialogBackdrop="z-[1000]"
      onClose={handleCancel}
      open={open}
      className={'w-[572px]'}
    >
      <div
        className={cn(
          'overflow-y-auto p-6',
          'relative flex w-full flex-col gap-4 rounded bg-white shadow-custom'
        )}
      >
        <div className="absolute right-2 top-2 z-[1000]">
          <Button
            size="small"
            variant="icon"
            color="neutral"
            className="!w-8 rounded-full"
            onClick={handleCancel}
            startAdornment={<CloseIcon />}
          />
        </div>
        <div className="flex flex-col">
          <Typography variant="medium4xl" className="text-tertiary-100 ">
            {t('context_library.modal_upload.title')}
          </Typography>
        </div>
        <FileAddContext onChange={loadFile} />
        {cardsFile.map(({ id, ...cardFile }) => (
          <CardFileContext
            key={id}
            onRemove={onRemove}
            onRetry={onRetry}
            onUpdateTags={onUpdateTags(id)}
            defaultTags={defaultTags}
            {...{ id, ...cardFile }}
          />
        ))}
        <div className="mt-6 flex justify-end">
          <Button
            size="large"
            variant="text"
            className="!w-8 rounded-full"
            onClick={handleCancel}
            label={t('buttons.cancel')}
          />
          <Button
            disabled={isDisabledUpload}
            size="large"
            variant="contained"
            className="!w-8 rounded-full"
            onClick={handleUpload}
            label={t('buttons.upload')}
            loading={uploading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ModalUploadContextLibrary;
